import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea
} from "./components/ui";
import { cn } from "./lib/utils";
import { Download, Loader2, PlugZap, UploadCloud } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { INVOICE_COUNTRIES } from "./invoiceCountries";

const EXT_ZIP_URL = process.env.REACT_APP_EXTENSION_ZIP_URL || `${process.env.PUBLIC_URL || ''}/nestos-invoice-extension.zip`;
// Desktop agent removed
// Backend API base for cross-domain calls
// Manually set per request to avoid env mismatches
const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};

const PANEL_API_URL = resolveApiBase();

const INVOICE_COPY = {
  en: {
    header: {
      title: "Invoice Downloader",
      subtitle: "Download invoices from SellerRunning, SellerFlash, and Amazon.com",
      badgeConnected: "Extension connected",
      badgeRequired: "Extension required"
    },
    platform: {
      cardTitle: "Platform settings",
      cardDescription: "Configure your download preferences.",
      selectLabel: "Select platform",
      selectPlaceholder: "Choose a platform",
      countryLabel: "Country",
      countryPlaceholder: "Select country",
      monthLabel: "Month",
      monthPlaceholder: "Select month",
      tokenLabel: "SellerFlash Token",
      tokenPlaceholder: "Bearer token for SellerFlash",
      tokenPlaceholderShort: "Bearer token",
      cookiesLabel: "Amazon Cookies",
      cookiesPlaceholder: "Paste your Amazon cookies here...",
      edit: "Edit",
      managed: "Managed by administrator"
    },
    platformOptions: {
      SF_BUYER: "SellerFlash Buyer Invoice",
      SF_ARADEPO: "SellerFlash Aradepo",
      SR_BUYER: "SellerRunning Buyer Invoice",
      SR_ARADEPO: "SellerRunning Aradepo",
      AMAZON: "Amazon.com Buyer Invoice",
      AMAZON_BUSINESS: "Amazon.com Business Invoice"
    },
    order: {
      cardTitle: "Order numbers",
      cardDescription: "Enter order numbers (one per line)",
      textareaPlaceholder: "Enter order numbers, one per line...",
      duplicatesTitle: (count) => `Duplicate order IDs detected (${count})`,
      duplicatesDescription: "Please remove duplicates and refresh the page before starting again.",
      buttonIdle: "Start download",
      buttonLoading: "Processing..."
    },
    amazon: {
      sellerLabel: "Amazon seller order IDs",
      sellerPlaceholder: "Enter Amazon seller order IDs, one per line...",
      buyerLabel: "Amazon buyer order IDs",
      buyerPlaceholder: "Enter Amazon buyer order IDs, one per line...",
      pairHint: "Each seller order ID line must align with the buyer order ID line in the same position.",
      missingPairError: "Enter both Amazon seller and buyer order IDs.",
      mismatchError: "Seller and buyer order lists need to have the same number of lines.",
      statusMissingBoth: "Please enter Amazon seller and buyer order IDs!",
      statusBothRequired: "Add both seller and buyer order IDs before starting the download.",
      statusMismatch: "Seller and buyer order lists must contain the same number of entries.",
      statusDuplicates: (list) => `Duplicate buyer order IDs detected. Please remove duplicates: ${list}`,
      duplicatesTitle: (count) => `Duplicate buyer order IDs detected (${count})`,
      duplicatesDescription: "Remove duplicate buyer order IDs and try again."
    },
    aradepo: {
      titles: {
        SF_ARADEPO: "SellerFlash Aradepo Upload",
        SR_ARADEPO: "SellerRunning Aradepo Upload"
      },
      description: "Upload .xlsx with columns including Order Number, Payment Type, Order Country.",
      uploadLabel: "Upload .xlsx",
      directTitle: "Use Direct API (Stripe URLs)",
      directDescription: "Fetch invoice links directly before letting the extension handle downloads.",
      startButton: "Start Aradepo",
      reviewTitle: "Review invoice URLs",
      ordersBadge: (count) => `${count} orders`,
      urlPlaceholder: "https://invoice.stripe.com/i/...",
      paymentPlaceholder: "Payment type",
      downloadButton: "Download from URLs",
      downloading: "Downloading..."
    },
    status: {
      cardTitle: "Status & results",
      cardDescription: "Monitor the active download job and review outcomes.",
      agentTitle: "Desktop agent job created",
      agentSteps: {
        step1: (platformName) => `Make sure you're logged into ${platformName} in Chrome.`,
        step2: "Run the desktop invoice agent to process the job.",
        step3: "Files will be saved to Desktop/Nestos Muhasebe Programı Raporları/."
      },
      metrics: {
        success: "Successful",
        failed: "Failed",
        duplicates: "Duplicates"
      },
      failedTitle: "Failed orders",
      duplicateTitle: "Duplicate orders"
    },
    extension: {
      cardTitle: "Chrome extension",
      cardDescription: "Install or re-download the Nestos Shop Invoice Chrome extension.",
      connectedTitle: "Chrome extension connected",
      connectedDescription:
        "The extension is detected and ready. If you need to reinstall or share it, use the download link below.",
      requiredTitle: "Chrome extension required",
      instructions: [
        { text: "Open Chrome Extensions page:", code: "chrome://extensions/" },
        { text: 'Enable "Developer mode" (toggle in top right)' },
        { text: 'Click "Load unpacked"' },
        { text: "Select the nestos-invoice-extension folder" },
        { text: "Refresh this page" }
      ],
      locationLabel: "Extension location:",
      locationDescription: "The extension files are in your project folder at",
      downloadButton: "Download ZIP",
      hintPrefix: "Hint: unzip and load with",
      hintCode: "chrome://extensions",
      hintSuffix: "→ Load unpacked"
    },
    info: {
      cardTitle: "How it works",
      cardDescription: "Understand how Nestos automation handles each platform.",
      items: [
        { label: "SellerRunning:", description: "Downloads invoices directly from SellerRunning panel." },
        { label: "SellerFlash:", description: "Downloads invoices from SellerFlash panel." },
        { label: "Amazon.com:", description: "Downloads buyer invoices from Amazon." },
        { label: "File location:", description: "Files saved to Downloads/invoices_by_app/." },
        { label: "Requirements:", description: "You must be logged into the respective platforms in Chrome." }
      ]
    },
    badges: {
      connected: "Connected",
      notConnected: "Not connected"
    }
  },
  tr: {
    header: {
      title: "Fatura indirici",
      subtitle: "SellerRunning, SellerFlash ve Amazon.com'dan faturaları indirin",
      badgeConnected: "Uzantı bağlı",
      badgeRequired: "Uzantı gerekli"
    },
    platform: {
      cardTitle: "Platform ayarları",
      cardDescription: "İndirme tercihlerinizi yapılandırın.",
      selectLabel: "Platform seçin",
      selectPlaceholder: "Bir platform seçin",
      countryLabel: "Ülke",
      countryPlaceholder: "Ülke seçin",
      monthLabel: "Ay",
      monthPlaceholder: "Ay seçin",
      tokenLabel: "SellerFlash belirteci",
      tokenPlaceholder: "SellerFlash için bearer belirteci",
      tokenPlaceholderShort: "Bearer belirteci",
      cookiesLabel: "Amazon Çerezleri",
      cookiesPlaceholder: "Amazon çerezlerinizi buraya yapıştırın...",
      edit: "Düzenle",
      managed: "Yönetici tarafından yönetiliyor"
    },
    platformOptions: {
      SF_BUYER: "SellerFlash Alıcı Faturası",
      SF_ARADEPO: "SellerFlash Aradepo",
      SR_BUYER: "SellerRunning Alıcı Faturası",
      SR_ARADEPO: "SellerRunning Aradepo",
      AMAZON: "Amazon.com Alıcı Faturası",
      AMAZON_BUSINESS: "Amazon.com Business Faturası"
    },
    order: {
      cardTitle: "Sipariş numaraları",
      cardDescription: "Sipariş numaralarını (satır başına bir adet) girin",
      textareaPlaceholder: "Sipariş numaralarını, her satıra bir tane olacak şekilde girin...",
      duplicatesTitle: (count) => `Yinelenen sipariş ID'leri tespit edildi (${count})`,
      duplicatesDescription: "Lütfen yinelenenleri kaldırıp sayfayı yeniledikten sonra yeniden deneyin.",
      buttonIdle: "İndirmeyi başlat",
      buttonLoading: "İşleniyor..."
    },
    amazon: {
      sellerLabel: "Amazon satıcı sipariş ID'leri",
      sellerPlaceholder: "Amazon satıcı sipariş ID'lerini, satır başına bir tane olacak şekilde girin...",
      buyerLabel: "Amazon alıcı sipariş ID'leri",
      buyerPlaceholder: "Amazon alıcı sipariş ID'lerini, satır başına bir tane olacak şekilde girin...",
      pairHint: "Her satırdaki satıcı ID, aynı satırdaki alıcı ID ile eşleşmelidir.",
      missingPairError: "Amazon satıcı ve alıcı sipariş ID'lerini birlikte girin.",
      mismatchError: "Satıcı ve alıcı listelerinin satır sayıları birebir aynı olmalıdır.",
      statusMissingBoth: "Lütfen Amazon satıcı ve alıcı sipariş ID'lerini girin!",
      statusBothRequired: "İndirmeyi başlatmadan önce hem satıcı hem alıcı sipariş ID'lerini ekleyin.",
      statusMismatch: "Satıcı ve alıcı sipariş listeleri aynı sayıda kayıt içermelidir.",
      statusDuplicates: (list) => `Yinelenen alıcı sipariş ID'leri tespit edildi. Lütfen kaldırıp tekrar deneyin: ${list}`,
      duplicatesTitle: (count) => `Yinelenen alıcı sipariş ID'leri tespit edildi (${count})`,
      duplicatesDescription: "Yinelenen alıcı sipariş ID'lerini kaldırıp tekrar deneyin."
    },
    aradepo: {
      titles: {
        SF_ARADEPO: "SellerFlash Aradepo yükleme",
        SR_ARADEPO: "SellerRunning Aradepo yükleme"
      },
      description: "Sipariş numarası, ödeme tipi ve sipariş ülkesi sütunlarını içeren .xlsx dosyasını yükleyin.",
      uploadLabel: ".xlsx yükle",
      directTitle: "Doğrudan API kullan (Stripe URL'leri)",
      directDescription: "Uzantının indirmesine izin vermeden önce fatura bağlantılarını doğrudan çekin.",
      startButton: "Aradepo'yu başlat",
      reviewTitle: "Fatura URL'lerini gözden geçir",
      ordersBadge: (count) => `${count} sipariş`,
      urlPlaceholder: "https://invoice.stripe.com/i/...",
      paymentPlaceholder: "Ödeme türü",
      downloadButton: "URL'lerden indir",
      downloading: "İndiriliyor..."
    },
    status: {
      cardTitle: "Durum ve sonuçlar",
      cardDescription: "Aktif indirme işini izleyin ve sonuçları gözden geçirin.",
      agentTitle: "Masaüstü ajan işi oluşturuldu",
      agentSteps: {
        step1: (platformName) => `${platformName} hesabınızla Chrome'da oturum açtığınızdan emin olun.`,
        step2: "İşi işlemek için masaüstü fatura ajanını çalıştırın.",
        step3: "Dosyalar Desktop/Nestos Muhasebe Programı Raporları/ konumuna kaydedilir."
      },
      metrics: {
        success: "BAŞARILI",
        failed: "BAŞARISIZ",
        duplicates: "YİNELENEN"
      },
      failedTitle: "Başarısız siparişler",
      duplicateTitle: "Yinelenen siparişler"
    },
    extension: {
      cardTitle: "Chrome uzantısı",
      cardDescription: "Nestos Shop Invoice Chrome uzantısını yükleyin veya yeniden indirin.",
      connectedTitle: "Chrome uzantısı bağlı",
      connectedDescription:
        "Uzantı algılandı ve hazır. Yeniden kurmanız veya paylaşmanız gerekirse aşağıdaki indirme bağlantısını kullanın.",
      requiredTitle: "Chrome uzantısı gerekli",
      instructions: [
        { text: "Chrome uzantıları sayfasını açın:", code: "chrome://extensions/" },
        { text: '"Geliştirici modunu" etkinleştirin (sağ üstteki anahtar)' },
        { text: '"Paketlenmemiş yükle" seçeneğine tıklayın' },
        { text: "nestos-invoice-extension klasörünü seçin" },
        { text: "Bu sayfayı yenileyin" }
      ],
      locationLabel: "Uzantı konumu:",
      locationDescription: "Uzantı dosyaları proje klasörünüzde burada bulunur",
      downloadButton: "ZIP indir",
      hintPrefix: "İpucu: zip dosyasını açıp",
      hintCode: "chrome://extensions",
      hintSuffix: "→ Paketlenmemiş yükle seçeneğiyle yükleyin"
    },
    info: {
      cardTitle: "Nasıl çalışır",
      cardDescription: "Nestos otomasyonunun her platformu nasıl yönettiğini öğrenin.",
      items: [
        { label: "SellerRunning:", description: "Faturaları doğrudan SellerRunning panelinden indirir." },
        { label: "SellerFlash:", description: "Faturaları SellerFlash panelinden indirir." },
        { label: "Amazon.com:", description: "Amazon'dan alıcı faturalarını indirir." },
        { label: "Dosya konumu:", description: "Dosyalar Downloads/invoices_by_app/ klasörüne kaydedilir." },
        { label: "Gereksinimler:", description: "Chrome'da ilgili platformlarda oturum açmış olmalısınız." }
      ]
    },
    badges: {
      connected: "Bağlı",
      notConnected: "Bağlı değil"
    }
  }
};

const Invoices = ({ token, role }) => {
    const { language } = useLanguage();
    const copy = INVOICE_COPY[language] || INVOICE_COPY.en;
    const [platform, setPlatform] = useState('SR_BUYER');
    const [country, setCountry] = useState('Canada');
    const [month, setMonth] = useState('March');
    const [orderNumbers, setOrderNumbers] = useState('');
    const [amazonSellerOrderNumbers, setAmazonSellerOrderNumbers] = useState('');
    const [amazonBuyerOrderNumbers, setAmazonBuyerOrderNumbers] = useState('');
    const [warehouseFile, setWarehouseFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('Checking for Chrome extension...');
    const [results, setResults] = useState(null);
    const [extensionAvailable, setExtensionAvailable] = useState(false);
    const [preflightDuplicates, setPreflightDuplicates] = useState([]);
    // Direct API (Stripe URLs) flow for SellerFlash Aradepo
    const [useDirectApi, setUseDirectApi] = useState(true);
    const [apiToken, setApiToken] = useState('');
    const [sellerFlashReachable, setSellerFlashReachable] = useState(null);
    const [directUrls, setDirectUrls] = useState([]);
    const [directDownloading, setDirectDownloading] = useState(false);

    const isAdmin = role === 'admin';

    const fetchSettings = useCallback(async () => {
        if (!token) {
            setApiToken('');
            return;
        }
        try {
            const response = await axios.get(`${PANEL_API_URL}/settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApiToken(response.data?.sellerflash_bearer_token || '');
        } catch (error) {
            console.error('Failed to fetch settings', error);
            setApiToken('');
        }
    }, [token]);

    const checkSellerFlashConnectivity = useCallback(async (bearer) => {
        if (!bearer) {
            setSellerFlashReachable(false);
            return;
        }
        try {
            const response = await axios.get("https://gw.sellerflash.com/api/customerSubscription/get", {
                headers: {
                    Authorization: `Bearer ${bearer}`
                },
                // Cross-origin requests may fail silently if CORS blocks them; treat non-2xx as unreachable
                validateStatus: (status) => status >= 200 && status < 500
            });
            setSellerFlashReachable(response.status === 200);
        } catch (error) {
            console.error("SellerFlash connectivity check failed", error);
            setSellerFlashReachable(false);
        }
    }, []);

    useEffect(() => {
        const initialise = async () => {
            await fetchSettings();
        };
        initialise();
    }, [fetchSettings]);

    useEffect(() => {
        checkSellerFlashConnectivity(apiToken);
    }, [apiToken, checkSellerFlashConnectivity]);

    const platformOptions = [
        { value: 'SF_BUYER', label: 'SellerFlash Buyer Invoice' },
        { value: 'SF_ARADEPO', label: 'SellerFlash Aradepo' },
        { value: 'SR_BUYER', label: 'SellerRunning Buyer Invoice' },
        { value: 'SR_ARADEPO', label: 'SellerRunning Aradepo' },
        { value: 'AMAZON', label: 'Amazon.com Buyer Invoice' },
        { value: 'AMAZON_BUSINESS', label: 'Amazon.com Business Invoice' }
    ];

    const countries = INVOICE_COUNTRIES;

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const isAmazonPlatform = platform === 'AMAZON' || platform === 'AMAZON_BUSINESS';
    const amazonSellerEntries = useMemo(() => {
        if (!isAmazonPlatform) return [];
        return amazonSellerOrderNumbers
            .split(/\r?\n/)
            .map((value) => value.trim())
            .filter(Boolean);
    }, [isAmazonPlatform, amazonSellerOrderNumbers]);

    const amazonBuyerEntries = useMemo(() => {
        if (!isAmazonPlatform) return [];
        return amazonBuyerOrderNumbers
            .split(/\r?\n/)
            .map((value) => value.trim())
            .filter(Boolean);
    }, [isAmazonPlatform, amazonBuyerOrderNumbers]);

    const amazonHasBothLists = isAmazonPlatform && amazonSellerEntries.length > 0 && amazonBuyerEntries.length > 0;
    const amazonPairMismatch = isAmazonPlatform && amazonHasBothLists && amazonSellerEntries.length !== amazonBuyerEntries.length;

    const amazonInlineError = useMemo(() => {
        if (!isAmazonPlatform) return '';
        const sellerHasValues = amazonSellerEntries.length > 0;
        const buyerHasValues = amazonBuyerEntries.length > 0;

        if (!sellerHasValues && !buyerHasValues) {
            return '';
        }
        if (!sellerHasValues || !buyerHasValues) {
            return copy.amazon.missingPairError;
        }
        if (amazonPairMismatch) {
            return copy.amazon.mismatchError;
        }
        return '';
    }, [amazonBuyerEntries, amazonPairMismatch, amazonSellerEntries, isAmazonPlatform, copy.amazon]);

    const downloadDisabled =
        isProcessing ||
        (!extensionAvailable) || // Extension required for all platforms
        preflightDuplicates.length > 0 ||
        (isAmazonPlatform
            ? !amazonHasBothLists || amazonPairMismatch || Boolean(amazonInlineError)
            : !orderNumbers.trim());

    const handlePlatformChange = (newPlatform) => {
        setPlatform(newPlatform);
        // no-op (order type is derived from platform)
    };
    // Normalize initial platform if it doesn't match any option (prevents wrong default render)
    useEffect(() => {
        try {
            const valid = platformOptions.some(p => p.value === platform);
            if (!valid) setPlatform('SR_BUYER');
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Desktop agent removed

    // Check for extension availability on component mount
    useEffect(() => {
        // Listen for extension ready event on both window and document FIRST
        const handleExtensionReady = () => {
            console.log('Extension ready event received');
            setExtensionAvailable(true);
            setStatus('Chrome extension connected! Ready to download invoices...');
        };

        window.addEventListener('nestosInvoiceExtensionReady', handleExtensionReady);
        document.addEventListener('nestosInvoiceExtensionReady', handleExtensionReady);

        // Then check via DOM attribute which is visible across worlds
        const checkExtension = () => {
            try {
                const attr = document.documentElement?.getAttribute('data-nestos-invoice-extension');
                if (attr === 'ready') {
                    console.log('Extension detected via DOM attribute');
                    setExtensionAvailable(true);
                    setStatus('Ready to download invoices...');
                } else {
                    console.log('Extension not detected (attribute missing)');
                    setExtensionAvailable(false);
                    setStatus('Chrome extension not detected. Please install the Nestos Shop Invoice extension.');
                }
            } catch (error) {
                console.error('Extension check error:', error);
                setExtensionAvailable(false);
                setStatus('Chrome extension not detected. Please install the Nestos Shop Invoice extension.');
            }
        };

        // Check immediately
        checkExtension();

        // Check only once more if not detected, then stop completely
        const timeoutId = setTimeout(() => {
            if (!extensionAvailable) {
                checkExtension();
            }
        }, 3000);

        return () => {
            window.removeEventListener('nestosInvoiceExtensionReady', handleExtensionReady);
            document.removeEventListener('nestosInvoiceExtensionReady', handleExtensionReady);
            clearTimeout(timeoutId);
        };
    }, [extensionAvailable]);

    useEffect(() => {
        if (!token) return;
        const controller = new AbortController();
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${PANEL_API_URL}/settings`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                });
                const storedToken = response.data?.sellerflash_bearer_token || '';
                setApiToken(storedToken);
            } catch (error) {
                console.error('Failed to fetch settings', error);
            } finally {
                // setInitialTokenLoaded(true); // This line is removed
            }
        };
        fetchSettings();
        return () => controller.abort();
    }, [token]);

    const handleDownload = async () => {
        const isAmazon = platform === 'AMAZON' || platform === 'AMAZON_BUSINESS';
        let releaseProcessingOnFinally = true;
        let rawList = [];
        let amazonSellerRows = [];

        setPreflightDuplicates([]);

        if (isAmazon) {
            const sellers = amazonSellerEntries;
            const buyers = amazonBuyerEntries;

            if (!sellers.length && !buyers.length) {
                setStatus(copy.amazon.statusMissingBoth);
                setIsProcessing(false);
                return;
            }

            if (!sellers.length || !buyers.length) {
                setStatus(copy.amazon.statusBothRequired);
                setIsProcessing(false);
                return;
            }

            if (sellers.length !== buyers.length) {
                setStatus(copy.amazon.statusMismatch);
                setIsProcessing(false);
                return;
            }

            amazonSellerRows = sellers;
            rawList = buyers;
        } else {
            rawList = orderNumbers.split('\n').map(o => o.trim()).filter(Boolean);
        }

        // Preflight duplicate detection
        const seen = new Set();
        const uniqueList = [];
        const dups = [];
        for (const id of rawList) {
            if (seen.has(id)) {
                dups.push(id);
            } else {
                seen.add(id);
                uniqueList.push(id);
            }
        }
        setPreflightDuplicates(dups);
        if (dups.length > 0) {
            const duplicatesSentence = Array.from(new Set(dups)).join(', ');
            if (isAmazon) {
                setStatus(copy.amazon.statusDuplicates(duplicatesSentence));
            } else {
                setStatus(`Duplicate order IDs detected. Please remove duplicates: ${duplicatesSentence}`);
            }
            setIsProcessing(false);
            return;
        }
        const orderList = uniqueList;
        
        if (!orderList.length) {
            if (isAmazon) {
                setStatus(copy.amazon.statusMissingBoth);
            } else {
                setStatus('Please enter order numbers!');
            }
            return;
        }

        if (platform === 'SF_ARADEPO' || platform === 'SR_ARADEPO') {
            // For warehouse flows we expect file upload path; handled by separate button
            setStatus('Please use the Warehouse upload section below.');
            return;
        }

        // Extension path requires extension
        if (!extensionAvailable) {
            setStatus('Chrome extension not detected! Please install the Nestos Shop Invoice extension.');
            return;
        }

        setIsProcessing(true);
        setStatus(`Starting download of ${orderList.length} orders...`);
        setResults(null);

        try {
            const platformForJob = (() => {
                switch (platform) {
                    case 'AMAZON':
                        return 'Amazon.com';
                    case 'AMAZON_BUSINESS':
                        return 'Amazon.com Business';
                    case 'SR_BUYER':
                        return 'SellerRunning';
                    case 'SF_BUYER':
                        return 'SellerFlash';
                    default:
                        return platform;
                }
            })();

            const computedOrderType = isAmazon ? 'Buyer Number' : 'Seller Number';
            const jobData = {
                platform: platformForJob,
                orderType: computedOrderType,
                country,
                month,
                orderNumbers: orderList,
                total: orderList.length,
                // Needed for extension -> backend upload (S3)
                apiBase: PANEL_API_URL,
                authToken: token
            };

            if (isAmazon) {
                const amazonPairs = orderList.map((buyerId, index) => ({
                    buyerOrderId: buyerId,
                    sellerOrderId: amazonSellerRows[index] || ''
                }));
                const amazonBuyerToSellerMap = {};
                orderList.forEach((buyerId, index) => {
                    amazonBuyerToSellerMap[buyerId] = amazonSellerRows[index] || '';
                });
                jobData.amazonOrderPairs = amazonPairs;
                jobData.amazonBuyerToSellerMap = amazonBuyerToSellerMap;
                jobData.amazonSellerOrderNumbers = amazonSellerRows;
            }

            if (platform === 'SF_BUYER') {
                // Extension CDP flow for SellerFlash Buyer Invoice
                setStatus(`Generating PDFs via extension... 0/${orderList.length}`);
                let processed = 0, ok = 0, failed = 0;
                const failedOrdersBuyer = [];
                for (const orderId of orderList) {
                    try {
                        // Resolve IDs
                        const idsResp = await fetch(`${PANEL_API_URL}/invoices/sf-buyer/get-ids`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-GW-Authorization': `Bearer ${apiToken}`,
                                'Authorization': token ? `Bearer ${token}` : ''
                            },
                            body: JSON.stringify({ orderId })
                        });
                        if (!idsResp.ok) {
                            let bodyText = '';
                            try { bodyText = await idsResp.text(); } catch {}
                            throw new Error(`get-ids ${idsResp.status}${bodyText ? ` - ${bodyText.slice(0, 180)}` : ''}`);
                        }
                        const ids = await idsResp.json();
                        let sellerFlashCurrency = (ids.currency || '').trim().toUpperCase();
                        if (!sellerFlashCurrency) {
                            try {
                                const currencyResp = await fetch(
                                    `https://gw.sellerflash.com/api/sellerOrder/getSellerOrderDetails?sellerAmazonOrderId=${encodeURIComponent(orderId)}`,
                                    {
                                        method: 'GET',
                                        headers: {
                                            Authorization: `Bearer ${apiToken}`
                                        }
                                    }
                                );
                                if (currencyResp.ok) {
                                    const currencyJson = await currencyResp.json();
                                    const currencyValue =
                                        currencyJson?.marketPlace?.currency ||
                                        currencyJson?.data?.marketPlace?.currency ||
                                        currencyJson?.currency ||
                                        '';
                                    if (typeof currencyValue === 'string' && currencyValue.trim()) {
                                        sellerFlashCurrency = currencyValue.trim().toUpperCase();
                                    }
                                }
                            } catch (currencyError) {
                                console.error('Failed to fetch SellerFlash order details for currency', currencyError);
                            }
                        }
                        // Fetch invoice JSON
                        const invResp = await fetch(`${PANEL_API_URL}/invoices/sf-buyer/invoice-data`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-GW-Authorization': `Bearer ${apiToken}`,
                                'Authorization': token ? `Bearer ${token}` : ''
                            },
                            body: JSON.stringify({
                                sellerOrderId: ids.sellerOrderId,
                                customerMarketPlaceId: ids.customerMarketPlaceId
                            })
                        });
                        if (!invResp.ok) {
                            let bodyText2 = '';
                            try { bodyText2 = await invResp.text(); } catch {}
                            throw new Error(`invoice-data ${invResp.status}${bodyText2 ? ` - ${bodyText2.slice(0, 180)}` : ''}`);
                        }
                        const invoiceJson = await invResp.json();
                        // Merge promotion discount from ids into invoice data payload
                        const mergedData = (() => {
                            const base = (invoiceJson && invoiceJson.data) ? invoiceJson.data : invoiceJson;
                            const promo = Number(ids && ids.promotionDiscountTotal ? ids.promotionDiscountTotal : 0);
                            try {
                                const enriched = { ...base, promotionDiscountTotal: promo };
                                if (sellerFlashCurrency) {
                                    enriched.currency = sellerFlashCurrency;
                                }
                                return enriched;
                            } catch {
                                base.promotionDiscountTotal = promo;
                                if (sellerFlashCurrency) {
                                    base.currency = sellerFlashCurrency;
                                }
                                return base;
                            }
                        })();
                        // Ask extension to open generator and CDP-print to nested filename
                        const generatorUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/invoice_generator_nestos.html`;
                        await new Promise((resolve) => {
                            let settled = false;
                            const onResult = (e) => {
                                const d = e.detail || {};
                                // We rely on download completion events for final result; this ack just unblocks flow
                                if (settled) return;
                                settled = true;
                                document.removeEventListener('nestosInvoiceSFBResult', onResult);
                                resolve(d);
                            };
                            document.addEventListener('nestosInvoiceSFBResult', onResult, { once: true });
                            try {
                                document.dispatchEvent(new CustomEvent('nestosInvoiceSFBGenerate', {
                                    detail: {
                                        generatorUrl,
                                        orderId,
                                        sellerOrderId: ids.sellerOrderId,
                                        month,
                                        country,
                                        data: mergedData
                                    }
                                }));
                            } catch {
                                document.removeEventListener('nestosInvoiceSFBResult', onResult);
                                resolve({ error: 'dispatch failed' });
                            }
                            setTimeout(() => {
                                if (settled) return;
                                settled = true;
                                document.removeEventListener('nestosInvoiceSFBResult', onResult);
                                resolve({ timeout: true });
                            }, 3000);
                        });
                        ok++;
                    } catch (e) {
                        failed++;
                        console.error(`Order ${orderId} failed:`, e);
                        failedOrdersBuyer.push(`${orderId}: ${e && e.message ? e.message : 'Request failed'}`);
                    } finally {
                        processed++;
                        setStatus(`Generating PDFs via extension... ${processed}/${orderList.length}`);
                    }
                }
                setResults({
                    successful: ok,
                    failed: failed,
                    duplicates: 0,
                    total_orders: orderList.length,
                    failedOrders: failedOrdersBuyer
                });
                setStatus(`Completed: ${ok} successful, ${failed} failed`);
            } else {
                // Use DOM event bridge to communicate with the extension
                // Keep UI in "processing" state until monitorExtensionProgress finishes
                releaseProcessingOnFinally = false;
                await new Promise((resolve, reject) => {
                    let settled = false;
                    const onResponse = (e) => {
                        if (settled) return;
                        settled = true;
                        resolve(e.detail || { success: true, message: 'Started' });
                    };
                    document.addEventListener('nestosInvoiceResponse', onResponse, { once: true });
                    try {
                        document.dispatchEvent(new CustomEvent('nestosInvoiceDownload', { detail: jobData }));
                    } catch (err) {
                        document.removeEventListener('nestosInvoiceResponse', onResponse);
                        reject(err);
                        return;
                    }
                    setTimeout(() => {
                        if (settled) return;
                        settled = true;
                        document.removeEventListener('nestosInvoiceResponse', onResponse);
                        resolve({ success: true, message: 'Started (no immediate response)' });
                    }, 1500);
                });
                setStatus('Download job started! Extension is processing your orders...');
                setResults({
                    job_started: true,
                    total_orders: orderList.length,
                    platform: platform
                });
                // Monitor progress via event bridge
                console.log('🔄 Starting progress monitoring...');
                monitorExtensionProgress();
            }
            
        } catch (error) {
            setStatus(`Error: ${error.message}`);
        } finally {
            if (releaseProcessingOnFinally) {
                setIsProcessing(false);
            }
        }
    };

    // Desktop agent progress removed

    const monitorExtensionProgress = () => {
        let monitoringActive = true;
        let checkCount = 0;
        // Polling guard: 40 checks (~80s) is too short for large Amazon jobs.
        // Keep polling long enough for big batches (47+ orders can take many minutes).
        const maxChecks = 3600; // ~120 minutes at 2s interval
        let intervalId = null;

        const onStatus = (e) => {
            const response = e.detail || {};
            if (!monitoringActive) return;
            const job = response.currentJob;
            if (!job) return;

            if (job.status === 'processing') {
                const currentProgress = job.processed || 0;
                const totalOrders = job.total || 0;
                const statusText = `Processing ${currentProgress}/${totalOrders} orders...`;
                setStatus(statusText);
                setResults({
                    successful: job.successful || 0,
                    failed: job.failedOrders ? job.failedOrders.length : 0,
                    duplicates: job.duplicates || 0,
                    total_orders: totalOrders
                });
            } else if (job.status === 'completed') {
                const successCount = job.successful || 0;
                const failedCount = job.failedOrders ? job.failedOrders.length : 0;
                const duplicateCount = job.duplicates || 0;
                
                let statusText = `Download completed! ${successCount} successful`;
                if (failedCount > 0) statusText += `, ${failedCount} failed`;
                if (duplicateCount > 0) statusText += `, ${duplicateCount} duplicates`;
                
                setStatus(statusText);
                setResults({
                    successful: successCount,
                    failed: failedCount,
                    duplicates: duplicateCount,
                    total_orders: job.total || 0,
                    failedOrders: job.failedOrders || [],
                    duplicateOrders: job.duplicateOrders || []
                });
                setIsProcessing(false);
                monitoringActive = false;
                if (intervalId) clearInterval(intervalId);
                document.removeEventListener('nestosInvoiceStatus', onStatus);
            } else if (job.status === 'cancelled') {
                const successCount = job.successful || 0;
                const failedCount = job.failedOrders ? job.failedOrders.length : 0;
                const duplicateCount = job.duplicates || 0;
                setStatus(`Download cancelled. ${successCount} successful, ${failedCount} failed, ${duplicateCount} duplicates`);
                setResults({
                    successful: successCount,
                    failed: failedCount,
                    duplicates: duplicateCount,
                    total_orders: job.total || 0,
                    failedOrders: job.failedOrders || [],
                    duplicateOrders: job.duplicateOrders || []
                });
                setIsProcessing(false);
                monitoringActive = false;
                if (intervalId) clearInterval(intervalId);
                document.removeEventListener('nestosInvoiceStatus', onStatus);
            }
        };

        document.addEventListener('nestosInvoiceStatus', onStatus);

        intervalId = setInterval(() => {
            if (!monitoringActive || checkCount >= maxChecks) {
                console.log('📊 Stopping progress monitoring - max checks reached');
                setIsProcessing(false);
                setStatus('Download monitoring stopped - check extension popup for results (job may still be running)');
                if (intervalId) clearInterval(intervalId);
                document.removeEventListener('nestosInvoiceStatus', onStatus);
                return;
            }
            checkCount++;
            document.dispatchEvent(new CustomEvent('nestosInvoiceGetStatus'));
        }, 2000);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold text-foreground">{copy.header.title}</h1>
                    <p className="text-sm text-muted-foreground">
                        {copy.header.subtitle}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div
                        className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm",
                            extensionAvailable
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"
                                : "border-amber-500/40 bg-amber-500/10 text-amber-600"
                        )}
                    >
                        <span className="flex h-2 w-2 rounded-full bg-current" />
                        {extensionAvailable ? copy.header.badgeConnected : copy.header.badgeRequired}
                    </div>
                </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
                <Card className="border-border/60 bg-card/70">
                    <CardHeader>
                        <CardTitle>{copy.platform.cardTitle}</CardTitle>
                        <CardDescription>{copy.platform.cardDescription}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">{copy.platform.selectLabel}</label>
                            <Select value={platform} onValueChange={handlePlatformChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder={copy.platform.selectPlaceholder} />
                                </SelectTrigger>
                                <SelectContent>
                                    {platformOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {copy.platformOptions[option.value] || option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            </div>

                            {(platform !== 'SF_ARADEPO' && platform !== 'SR_ARADEPO') && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-muted-foreground">{copy.platform.countryLabel}</label>
                                <Select value={country} onValueChange={setCountry}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={copy.platform.countryPlaceholder} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {countries.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                </div>
                            )}

                        {(platform === 'AMAZON' || platform === 'AMAZON_BUSINESS' || platform === 'SF_ARADEPO' || platform === 'SR_ARADEPO' || platform === 'SF_BUYER') && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-muted-foreground">{copy.platform.monthLabel}</label>
                                <Select value={month} onValueChange={setMonth}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={copy.platform.monthPlaceholder} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {months.map((m) => (
                                            <SelectItem key={m} value={m}>
                                                {m}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                </div>
                        )}

                            {platform === 'SF_BUYER' && (
                            <div className="space-y-3">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-muted-foreground">{copy.platform.tokenLabel}</label>
                                    <Badge
                                                variant="outline"
                                        className={cn(
                                                    "inline-flex items-center gap-1 text-xs bg-transparent",
                                                    sellerFlashReachable
                                                        ? "border-emerald-200 text-emerald-700"
                                                        : "border-amber-300 text-amber-600"
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "h-2 w-2 rounded-full",
                                                sellerFlashReachable ? "bg-emerald-500" : "bg-amber-500"
                                            )}
                                        />
                                                {sellerFlashReachable ? copy.badges.connected : copy.badges.notConnected}
                                    </Badge>
                                        </div>
                                    {isAdmin ? (
                                        <Button asChild variant="outline" size="sm" className="w-fit gap-2">
                                                <Link to="/settings">{copy.platform.edit}</Link>
                                        </Button>
                                    ) : (
                                            <span className="text-xs text-muted-foreground">{copy.platform.managed}</span>
                                    )}
                                    </div>
                                </div>
                                <Input
                                    type="password"
                                    value={apiToken}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setApiToken(value);
                                    }}
                                    placeholder={copy.platform.tokenPlaceholder}
                                    autoComplete="new-password"
                                    readOnly={!isAdmin}
                                />
                                </div>
                            )}

                            {platform === 'AMAZON_BUSINESS' && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">
                                        Amazon.com Business invoices are generated via the Chrome extension using your logged-in Amazon session.
                                    </p>
                                </div>
                            )}
                    </CardContent>
                </Card>

                <Card className="border-border/60 bg-card/70">
                    <CardHeader>
                        <CardTitle>{copy.order.cardTitle}</CardTitle>
                        <CardDescription>{copy.order.cardDescription}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {isAmazonPlatform ? (
                            <div className="space-y-3">
                                <div className="grid gap-3 lg:grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-muted-foreground">
                                            {copy.amazon.sellerLabel}
                                        </label>
                                        <Textarea
                                            value={amazonSellerOrderNumbers}
                                            onChange={(event) => setAmazonSellerOrderNumbers(event.target.value)}
                                            placeholder={copy.amazon.sellerPlaceholder}
                                            className="min-h-[220px]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-muted-foreground">
                                            {copy.amazon.buyerLabel}
                                        </label>
                                        <Textarea
                                            value={amazonBuyerOrderNumbers}
                                            onChange={(event) => setAmazonBuyerOrderNumbers(event.target.value)}
                                            placeholder={copy.amazon.buyerPlaceholder}
                                            className="min-h-[220px]"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground">{copy.amazon.pairHint}</p>
                                {amazonInlineError && (
                                    <p className="text-xs font-medium text-destructive">{amazonInlineError}</p>
                                )}
                            </div>
                        ) : (
                            <Textarea
                                value={orderNumbers}
                                onChange={(event) => setOrderNumbers(event.target.value)}
                                placeholder={copy.order.textareaPlaceholder}
                                className="min-h-[220px]"
                            />
                        )}

                        {preflightDuplicates.length > 0 && (
                            <Alert variant="destructive">
                                <AlertTitle>
                                    {(isAmazonPlatform ? copy.amazon.duplicatesTitle : copy.order.duplicatesTitle)(
                                        Array.from(new Set(preflightDuplicates)).length
                                    )}
                                </AlertTitle>
                                <AlertDescription>
                                    <p className="text-sm">
                                        {isAmazonPlatform ? copy.amazon.duplicatesDescription : copy.order.duplicatesDescription}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {Array.from(new Set(preflightDuplicates)).map((id) => (
                                            <span
                                                key={id}
                                                className="rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
                                            >
                                            {id}
                                        </span>
                                    ))}
                                </div>
                                </AlertDescription>
                            </Alert>
                        )}

                        <Button
                            onClick={handleDownload}
                            disabled={downloadDisabled}
                            className="w-full gap-2"
                        >
                            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            {isProcessing ? copy.order.buttonLoading : copy.order.buttonIdle}
                        </Button>

                        <Button
                            variant="destructive"
                            onClick={async () => {
                                try {
                                    setStatus('Cancelling...');
                                    document.dispatchEvent(new CustomEvent('nestosInvoiceCancelJob'));
                                } catch (e) {
                                    setStatus(`Cancel error: ${e && e.message ? e.message : 'Failed to cancel'}`);
                                }
                            }}
                            disabled={!isProcessing || !extensionAvailable}
                            className="w-full gap-2"
                        >
                            Stop
                        </Button>
                    </CardContent>
                </Card>
                </div>

                {(platform === 'SF_ARADEPO' || platform === 'SR_ARADEPO') && (
                <Card className="border-border/60 bg-card/70">
                    <CardHeader>
                        <CardTitle>
                            {copy.aradepo.titles[platform] || copy.aradepo.titles.SF_ARADEPO}
                        </CardTitle>
                        <CardDescription>{copy.aradepo.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">{copy.aradepo.uploadLabel}</label>
                            <Input
                                    type="file"
                                    accept=".xlsx,.csv"
                                onChange={(event) =>
                                    setWarehouseFile(
                                        event.target.files && event.target.files[0] ? event.target.files[0] : null
                                    )
                                }
                                />
                            </div>

                            {platform === 'SF_ARADEPO' && (
                            <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background/70 p-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-semibold text-foreground">{copy.aradepo.directTitle}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {copy.aradepo.directDescription}
                                    </p>
                                </div>
                                <Switch checked={useDirectApi} onCheckedChange={setUseDirectApi} />
                            </div>
                        )}

                        {platform === 'SF_ARADEPO' && useDirectApi && (
                            <div className="space-y-3">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-muted-foreground">{copy.platform.tokenLabel}</label>
                                    <Badge
                                                variant="outline"
                                        className={cn(
                                                    "inline-flex items-center gap-1 text-xs bg-transparent",
                                                    sellerFlashReachable
                                                        ? "border-emerald-200 text-emerald-700"
                                                        : "border-amber-300 text-amber-600"
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "h-2 w-2 rounded-full",
                                                sellerFlashReachable ? "bg-emerald-500" : "bg-amber-500"
                                            )}
                                        />
                                                {sellerFlashReachable ? copy.badges.connected : copy.badges.notConnected}
                                    </Badge>
                                        </div>
                                    {isAdmin ? (
                                        <Button asChild variant="outline" size="sm" className="w-fit gap-2">
                                                <Link to="/settings">{copy.platform.edit}</Link>
                                        </Button>
                                    ) : (
                                            <span className="text-xs text-muted-foreground">{copy.platform.managed}</span>
                                    )}
                                    </div>
                                </div>
                                <Input
                                            type="password"
                                            value={apiToken}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setApiToken(value);
                                    }}
                                            placeholder={copy.platform.tokenPlaceholderShort}
                                            autoComplete="new-password"
                                            readOnly={!isAdmin}
                                        />
                                </div>
                            )}

                        <Button
                                onClick={async () => {
                                    try {
                                        if (!extensionAvailable) {
                                            setStatus('Chrome extension not detected! Please install the Nestos Shop Invoice extension.');
                                            return;
                                        }
                                        if (!warehouseFile) {
                                            setStatus('Please choose an .xlsx file first.');
                                            return;
                                        }
                                        const { rows: orderRows, firstCountry } = await (async () => {
                                            const file = warehouseFile;
                                            const lower = (file.name || '').toLowerCase();
                                            if (lower.endsWith('.csv') || lower.endsWith('.tsv')) {
                                                const text = await file.text();
                                            const sep = lower.endsWith('.tsv') ? '\t' : ',';
                                            const lines = text.split(/\r?\n/).filter(Boolean);
                                                if (lines.length === 0) return { rows: [], firstCountry: '' };
                                            const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase());
                                            const idxOrder = headers.findIndex((h) => h.includes('order') && h.includes('number'));
                                            const idxPayType = headers.findIndex((h) => h.includes('payment') && h.includes('type'));
                                            const idxCountry = headers.findIndex((h) => h.includes('order') && h.includes('country'));
                                            const parsedRows = [];
                                                for (let i = 1; i < lines.length; i++) {
                                                    const cols = lines[i].split(sep);
                                                    const order = (cols[idxOrder] || '').trim();
                                                    if (!order) continue;
                                                    const pt = (idxPayType >= 0 ? cols[idxPayType] : '').trim();
                                                    const oc = (idxCountry >= 0 ? cols[idxCountry] : '').trim();
                                                parsedRows.push({
                                                        orderId: order,
                                                        paymentType: pt || '',
                                                        country: oc || ''
                                                    });
                                                }
                                            const fc = parsedRows.length > 0 ? parsedRows[0].country || '' : '';
                                            return { rows: parsedRows, firstCountry: fc };
                                            }
                                            if (!window.XLSX) {
                                                await new Promise((resolve, reject) => {
                                                const script = document.createElement('script');
                                                script.src = 'https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js';
                                                script.async = true;
                                                script.onload = () => resolve();
                                                script.onerror = () => reject(new Error('Failed to load XLSX parser'));
                                                document.head.appendChild(script);
                                                });
                                            }
                                            const data = await file.arrayBuffer();
                                        const workbook = window.XLSX.read(data, { type: 'array' });
                                        const ws = workbook.Sheets[workbook.SheetNames[0]];
                                        const rowsJson = window.XLSX.utils.sheet_to_json(ws, { defval: '' });
                                        const parsedRows = [];
                                        for (const row of rowsJson) {
                                                const lowerMap = {};
                                            Object.keys(row).forEach((key) => (lowerMap[key.toLowerCase()] = key));
                                            const orderKey = Object.keys(lowerMap).find((key) => key.includes('order') && key.includes('number'));
                                            const payTypeKey = Object.keys(lowerMap).find((key) => key.includes('payment') && key.includes('type'));
                                            const countryKey = Object.keys(lowerMap).find((key) => key.includes('order') && key.includes('country'));
                                                const order = orderKey ? (row[lowerMap[orderKey]] || '').toString().trim() : '';
                                                if (!order) continue;
                                                const pt = payTypeKey ? (row[lowerMap[payTypeKey]] || '').toString().trim() : '';
                                                const oc = countryKey ? (row[lowerMap[countryKey]] || '').toString().trim() : '';
                                            parsedRows.push({ orderId: order, paymentType: pt, country: oc });
                                            }
                                        const fc = parsedRows.length > 0 ? parsedRows[0].country || '' : '';
                                        return { rows: parsedRows, firstCountry: fc };
                                        })();
                                        if (!orderRows || orderRows.length === 0) {
                                            setStatus('No orders found in the file.');
                                            return;
                                        }
                                        if (platform === 'SF_ARADEPO' && useDirectApi) {
                                            setStatus(`Fetching Stripe URLs from API... 0/${orderRows.length}`);
                                            const out = [];
                                        let okCount = 0,
                                            processed = 0;
                                            for (const row of orderRows) {
                                                const oid = row.orderId;
                                                try {
                                                const resp = await fetch(
                                                    `https://gw.sellerflash.com/api/payment/getCustomerInvoicePdfByAmazonOrderId?amazonOrderId=${encodeURIComponent(
                                                        oid
                                                    )}`,
                                                    {
                                                        method: 'GET',
                                                        headers: {
                                                            Authorization: `Bearer ${apiToken}`,
                                                            Accept: 'application/json'
                                                        }
                                                    }
                                                );
                                                    if (!resp.ok) throw new Error(`API ${resp.status}`);
                                                    const data = await resp.json();
                                                    const url = data && data.invoiceUrl ? data.invoiceUrl : '';
                                                    if (url) okCount++;
                                                    out.push({
                                                        orderId: oid,
                                                        url: url || '',
                                                        country: row.country || firstCountry || country,
                                                    paymentType: row.paymentType || 'payment',
                                                        error: url ? '' : 'No URL'
                                                    });
                                                } catch (e) {
                                                    out.push({
                                                        orderId: oid,
                                                        url: '',
                                                        country: row.country || firstCountry || country,
                                                    paymentType: row.paymentType || 'payment',
                                                        error: e.message || 'Fetch error'
                                                    });
                                                } finally {
                                                    processed++;
                                                        setStatus(`Fetching Stripe URLs from API... ${processed}/${orderRows.length}`);
                                                }
                                            }
                                            setDirectUrls(out);
                                            setResults(null);
                                            setStatus(`Fetched ${okCount}/${orderRows.length} invoice URLs. Review and click "Download from URLs".`);
                                        } else {
                                        setStatus(
                                            `Starting ${platform === 'SF_ARADEPO' ? 'SellerFlash' : 'SellerRunning'} Aradepo for ${orderRows.length} orders...`
                                        );
                                            await new Promise((resolve, reject) => {
                                                let settled = false;
                                            const onResponse = (event) => {
                                                if (settled) return;
                                                settled = true;
                                                resolve(event.detail || {});
                                            };
                                                document.addEventListener('nestosInvoiceResponse', onResponse, { once: true });
                                                try {
                                                const orderNumbers2 = orderRows.map((row) => row.orderId);
                                                    const paymentTypeMap2 = {};
                                                    const countryMap2 = {};
                                                for (const row of orderRows) {
                                                    if (row && row.orderId) {
                                                        if (row.paymentType) paymentTypeMap2[row.orderId] = row.paymentType;
                                                        if (row.country) countryMap2[row.orderId] = row.country;
                                                        }
                                                    }
                                                document.dispatchEvent(
                                                    new CustomEvent('nestosInvoiceDownload', {
                                                        detail: {
                                                            platform: platform === 'SF_ARADEPO' ? 'SellerFlash' : 'SellerRunning',
                                                            platformKind:
                                                                platform === 'SF_ARADEPO'
                                                                    ? 'sellerflash_warehouse'
                                                                    : 'sellerrunning_warehouse',
                                                            orderType: 'Seller Number',
                                                            country: firstCountry || country,
                                                            month,
                                                            orderNumbers: orderNumbers2,
                                                            paymentTypeMap: paymentTypeMap2,
                                                            orderCountryMap: countryMap2
                                                        }
                                                    })
                                                );
                                                } catch (err) {
                                                    document.removeEventListener('nestosInvoiceResponse', onResponse);
                                                    reject(err);
                                                    return;
                                                }
                                                setTimeout(() => {
                                                    if (settled) return;
                                                    settled = true;
                                                    document.removeEventListener('nestosInvoiceResponse', onResponse);
                                                    resolve({ success: true });
                                                }, 1500);
                                            });
                                            setStatus('Download job started! Extension is processing your orders...');
                                            monitorExtensionProgress();
                                        }
                                    } catch (e) {
                                        setStatus(`Error: ${e.message}`);
                                    }
                                }}
                                disabled={isProcessing || !extensionAvailable}
                            className="w-full gap-2"
                            >
                            <UploadCloud className="h-4 w-4" />
                                {copy.aradepo.startButton}
                        </Button>

                            {platform === 'SF_ARADEPO' && useDirectApi && directUrls && directUrls.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-semibold text-foreground">{copy.aradepo.reviewTitle}</h4>
                                    <Badge variant="muted">{copy.aradepo.ordersBadge(directUrls.length)}</Badge>
                                </div>
                                <ScrollArea className="h-72 rounded-xl border border-border/60 bg-background/70 p-3">
                                    <div className="space-y-4">
                                        {directUrls.map((item, idx) => (
                                            <div
                                                key={`${item.orderId}-${idx}`}
                                                className="grid gap-2 md:grid-cols-[150px_1fr_160px_160px]"
                                            >
                                                <div className="font-mono text-xs font-medium text-muted-foreground">
                                                    {item.orderId}
                                                </div>
                                                <Input
                                                    value={item.url}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        setDirectUrls((previous) => {
                                                            const newList = [...previous];
                                                            newList[idx] = { ...newList[idx], url: value, error: '' };
                                                            return newList;
                                                        });
                                                    }}
                                                    placeholder={copy.aradepo.urlPlaceholder}
                                                />
                                                <Input
                                                    value={item.paymentType || 'payment'}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        setDirectUrls((previous) => {
                                                            const newList = [...previous];
                                                            newList[idx] = { ...newList[idx], paymentType: value };
                                                            return newList;
                                                        });
                                                    }}
                                                    placeholder={copy.aradepo.paymentPlaceholder}
                                                />
                                                <select
                                                    value={item.country || country}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        setDirectUrls((previous) => {
                                                            const copy = [...previous];
                                                            copy[idx] = { ...copy[idx], country: value };
                                                            return copy;
                                                        });
                                                    }}
                                                    className="rounded-md border border-border/60 bg-background px-2 py-2 text-sm"
                                                >
                                                    {countries.map((c) => (
                                                        <option key={c} value={c}>
                                                            {c}
                                                        </option>
                                                    ))}
                                                </select>
                                                {item.error && (
                                                    <p className="md:col-span-4 text-xs font-medium text-destructive">{item.error}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                                <Button
                                        onClick={async () => {
                                            try {
                                                if (!extensionAvailable) {
                                                    setStatus('Chrome extension not detected! Please install the Nestos Shop Invoice extension.');
                                                    return;
                                                }
                                                setDirectDownloading(true);
                                                setIsProcessing(true);
                                            let success = 0,
                                                failed = 0,
                                                duplicates = 0;
                                                const failedOrders = [];
                                                const duplicateOrders = [];
                                                const seenSuccess = new Set();
                                                for (const item of directUrls) {
                                                    if (!item.url) {
                                                        failed++;
                                                    const meta = ` (${item.country || country}) - (Type: ${item.paymentType || 'payment'})`;
                                                        failedOrders.push(`${item.orderId}: No URL${meta}`);
                                                    setStatus(
                                                        `Direct downloading... ${success + failed + duplicates}/${directUrls.length}`
                                                    );
                                                        continue;
                                                    }
                                                    const res = await new Promise((resolve) => {
                                                        let settled = false;
                                                    const onResult = (event) => {
                                                        const detail = event.detail || {};
                                                        if (detail.orderNumber !== item.orderId) return;
                                                            if (settled) return;
                                                            settled = true;
                                                            document.removeEventListener('nestosInvoiceDirectResult', onResult);
                                                        resolve(detail);
                                                        };
                                                        document.addEventListener('nestosInvoiceDirectResult', onResult);
                                                        try {
                                                        document.dispatchEvent(
                                                            new CustomEvent('nestosInvoiceDirectDownload', {
                                                                detail: {
                                                                    url: item.url,
                                                                    filename: `invoice_${item.orderId}.pdf`,
                                                                    orderNumber: item.orderId,
                                                                    platform: 'SellerFlash',
                                                                    country: item.country || country,
                                                                    platformType:
                                                                        platform === 'SF_ARADEPO'
                                                                            ? 'sellerflash_warehouse'
                                                                            : (platform === 'SR_ARADEPO'
                                                                                  ? 'sellerrunning_warehouse'
                                                                                  : (platform || 'unknown')
                                                                              )
                                                                                  .toString()
                                                                                  .toLowerCase(),
                                                                    month,
                                                                    paymentType: item.paymentType || 'payment'
                                                                }
                                                            })
                                                        );
                                                        } catch {
                                                            document.removeEventListener('nestosInvoiceDirectResult', onResult);
                                                            resolve({ error: 'dispatch failed' });
                                                        }
                                                        setTimeout(() => {
                                                            if (settled) return;
                                                            settled = true;
                                                            document.removeEventListener('nestosInvoiceDirectResult', onResult);
                                                            resolve({ timeout: true });
                                                        }, 30000);
                                                    });
                                                    const resp = res && res.response ? res.response : {};
                                                    const isSuccess = !!resp.success;
                                                    const payLower = (item.paymentType || 'payment').toLowerCase();
                                                    const isDuplicate = !!resp.duplicate || (resp.filename && /\(\d+\)\.pdf$/i.test(resp.filename));
                                                    if (isSuccess) {
                                                        const key = `${item.orderId}::${payLower}`;
                                                        if (isDuplicate || seenSuccess.has(key)) {
                                                            duplicates++;
                                                        const savedName = resp && resp.filename ? resp.filename.split('/').pop() : `${item.orderId}_${payLower}.pdf`;
                                                        const metaDup = ` (${item.country || country}) - (Type: ${item.paymentType || 'payment'})`;
                                                            duplicateOrders.push(`${item.orderId}: ${savedName}${metaDup}`);
                                                        } else {
                                                            success++;
                                                            seenSuccess.add(key);
                                                        }
                                                    } else {
                                                        failed++;
                                                    const errMsg =
                                                        (resp && resp.error) ||
                                                        (res && res.error) ||
                                                        (res && res.timeout ? 'No download activity (timeout)' : 'No response');
                                                    const meta = ` (${item.country || country}) - (Type: ${item.paymentType || 'payment'})`;
                                                        failedOrders.push(`${item.orderId}: ${errMsg}${meta}`);
                                                    }
                                                setStatus(
                                                    `Direct downloading... ${success + failed + duplicates}/${directUrls.length}`
                                                );
                                                }
                                                setResults({
                                                    successful: success,
                                                failed,
                                                duplicates,
                                                    total_orders: directUrls.length,
                                                failedOrders,
                                                duplicateOrders
                                                });
                                            setStatus(
                                                `Direct download completed: ${success} successful, ${failed} failed, ${duplicates} duplicates`
                                            );
                                            } finally {
                                                setDirectDownloading(false);
                                                setIsProcessing(false);
                                            }
                                        }}
                                        disabled={directDownloading || isProcessing}
                                    className="w-full gap-2"
                                    >
                                    {directDownloading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4" />
                                    )}
                                        {directDownloading ? copy.aradepo.downloading : copy.aradepo.downloadButton}
                                </Button>
                                </div>
                            )}
                    </CardContent>
                </Card>
                )}

            <Card className="border-border/60 bg-card/70">
                <CardHeader>
                    <CardTitle>{copy.status.cardTitle}</CardTitle>
                    <CardDescription>{copy.status.cardDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div
                        className={cn(
                            "rounded-2xl border px-4 py-3 text-sm font-medium shadow-sm",
                            isProcessing
                                ? "border-primary/40 bg-primary/10 text-primary"
                                : "border-border/60 bg-background/70 text-foreground"
                        )}
                    >
                        <div className="flex items-center gap-2">
                            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                            <span>{status}</span>
                        </div>
                        </div>
                        
                        {results && (
                        results.job_id ? (
                            <div className="space-y-4 rounded-2xl border border-border/60 bg-background/70 p-5">
                                <div>
                                    <h4 className="text-sm font-semibold text-foreground">{copy.status.agentTitle}</h4>
                                    <p className="text-xs text-muted-foreground">Job ID: {results.job_id}</p>
                                    <p className="text-xs text-muted-foreground">Platform: {results.platform}</p>
                                    <p className="text-xs text-muted-foreground">Total orders: {results.total_orders}</p>
                                        </div>
                                <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
                                    <li>{copy.status.agentSteps.step1(results.platform)}</li>
                                    <li>{copy.status.agentSteps.step2}</li>
                                    <li>{copy.status.agentSteps.step3}</li>
                                            </ol>
                                    </div>
                                ) : (
                            <div className="space-y-6">
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-600">
                                        <p className="text-xs uppercase tracking-wide">{copy.status.metrics.success}</p>
                                        <p className="text-2xl font-semibold">{results.successful || 0}</p>
                                        </div>
                                    <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-rose-500">
                                        <p className="text-xs uppercase tracking-wide">{copy.status.metrics.failed}</p>
                                        <p className="text-2xl font-semibold">{results.failed || 0}</p>
                                        </div>
                                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-600">
                                        <p className="text-xs uppercase tracking-wide">{copy.status.metrics.duplicates}</p>
                                        <p className="text-2xl font-semibold">{results.duplicates || 0}</p>
                                    </div>
                                        </div>
                                        
                                        {results.failedOrders && results.failedOrders.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-semibold text-rose-500">{copy.status.failedTitle}</h4>
                                        <ScrollArea className="h-32 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
                                            <div className="space-y-1 text-xs font-mono text-rose-600">
                                                    {results.failedOrders.map((orderId, index) => (
                                                    <div key={index}>{orderId}</div>
                                                    ))}
                                                </div>
                                        </ScrollArea>
                                            </div>
                                        )}
                                        
                                        {results.duplicateOrders && results.duplicateOrders.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-semibold text-amber-500">{copy.status.duplicateTitle}</h4>
                                        <ScrollArea className="h-32 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                                            <div className="space-y-1 text-xs font-mono text-amber-600">
                                                    {results.duplicateOrders.map((orderId, index) => (
                                                    <div key={index}>{orderId}</div>
                                                    ))}
                                                </div>
                                        </ScrollArea>
                                            </div>
                                )}
                            </div>
                        )
                        )}
                </CardContent>
            </Card>

            <Card className="border-border/60 bg-card/70">
                <CardHeader>
                    <CardTitle>{copy.extension.cardTitle}</CardTitle>
                    <CardDescription>{copy.extension.cardDescription}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                        {extensionAvailable ? (
                        <Alert className="border-emerald-500/40 bg-emerald-500/10">
                            <AlertTitle>{copy.extension.connectedTitle}</AlertTitle>
                            <AlertDescription>
                                {copy.extension.connectedDescription}
                            </AlertDescription>
                        </Alert>
                        ) : (
                        <Alert variant="destructive">
                            <AlertTitle>{copy.extension.requiredTitle}</AlertTitle>
                            <AlertDescription>
                                <ol className="list-decimal space-y-2 pl-5 text-sm">
                                    {copy.extension.instructions.map((instruction, index) => (
                                        <li key={index}>
                                            {instruction.text}{" "}
                                            {instruction.code ? <code>{instruction.code}</code> : null}
                                        </li>
                                    ))}
                                </ol>
                            </AlertDescription>
                        </Alert>
                        )}

                    <div className="rounded-xl border border-border/60 bg-background/70 p-4 text-sm">
                        <p>
                            <span className="font-semibold">{copy.extension.locationLabel}</span> {copy.extension.locationDescription}
                            <code className="ml-1 rounded bg-muted px-2 py-0.5 text-xs">nestos-invoice-extension/</code>
                        </p>
                        </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button asChild>
                            <a href={EXT_ZIP_URL} download>
                                {copy.extension.downloadButton}
                            </a>
                        </Button>
                        <p className="text-xs text-muted-foreground">
                            {copy.extension.hintPrefix}{" "}
                            <code className="rounded bg-muted px-2 py-0.5">{copy.extension.hintCode}</code>{" "}
                            {copy.extension.hintSuffix}
                        </p>
                        </div>
                </CardContent>
            </Card>

            <Card className="border-border/60 bg-card/70">
                <CardHeader>
                    <CardTitle>{copy.info.cardTitle}</CardTitle>
                    <CardDescription>{copy.info.cardDescription}</CardDescription>
                </CardHeader>
                <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        {copy.info.items.map((item, index) => (
                            <li key={index}>
                                <strong>{item.label}</strong> {item.description}
                        </li>
                        ))}
                        </ul>
                </CardContent>
            </Card>
        </div>
    );
};

export default Invoices;
